import { useHybridData } from "./useHybridData";
import { supabase } from "@/integrations/supabase/client";
import treatmentCategoriesIndex from "@/data/generated/treatment-categories.json";

export interface TreatmentCategory {
  id: string;
  title: string;
  description?: string;
  href: string;
  treatmentCount: number;
}

// Function to fetch dynamic updates if needed (e.g., if categories change often)
// For now, categories are very stable, so we might just rely on static build, 
// but implementing hybrid allows for "new categories" to appear if added to DB.
const fetchNewCategories = async () => {
  const { data, error } = await supabase
    .from("treatments")
    .select("category");

  if (error) {
    console.error("Error fetching treatment categories:", error);
    return null;
  }

  const categoryCounts: Record<string, number> = {};
  data.forEach((t) => {
    if (t.category) {
      categoryCounts[t.category] = (categoryCounts[t.category] || 0) + 1;
    }
  });

  return Object.entries(categoryCounts).map(([id, count]) => ({
    id,
    title: id.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    href: `/treatments/${id}/`,
    treatmentCount: count
  })).sort((a, b) => b.treatmentCount - a.treatmentCount);
};

const sortCategories = (a: TreatmentCategory, b: TreatmentCategory) => b.treatmentCount - a.treatmentCount;

export function useTreatmentCategories() {
  const { data, isStatic, isCheckingUpdates } = useHybridData<TreatmentCategory>({
    resourceName: "Treatment Categories",
    staticData: treatmentCategoriesIndex as unknown as TreatmentCategory[],
    fetchDynamic: fetchNewCategories,
    queryKey: ["treatment-categories"],
    sortFn: sortCategories,
    skipUpdates: true,
  });

  return {
    data,
    isLoading: false,
    isStatic,
    isCheckingUpdates
  };
}

// Deprecated refresh hook kept for compatibility if needed, but aliased to main hook logic?
// Original file had a separate refresh hook. We can just export a dummy or remove usage.
// Assuming we replace usage, but to be safe:
export function useTreatmentCategoriesRefresh() {
    return useTreatmentCategories();
}
